import type { PlatformRole, TenantRole } from '@/lib/api';
import type { Role } from '@/types';

export interface RoleAccessUser {
  roles?: string[];
  platformRoles?: PlatformRole[];
  platform_roles?: PlatformRole[];
}

function normalizedPlatformRoles(user: RoleAccessUser | null | undefined): PlatformRole[] {
  return user?.platformRoles ?? user?.platform_roles ?? [];
}

export function hasLegacyPlatformCompat(user: RoleAccessUser | null | undefined, roleCheck: Role | Role[]) {
  if (!user || normalizedPlatformRoles(user).length > 0) return false;
  const rolesToCheck = Array.isArray(roleCheck) ? roleCheck : [roleCheck];
  return (user.roles ?? []).some((role) => rolesToCheck.includes(role as Role));
}

export function hasPlatformRole(user: RoleAccessUser | null | undefined, roleCheck: PlatformRole | PlatformRole[]) {
  if (!user) return false;
  const rolesToCheck = Array.isArray(roleCheck) ? roleCheck : [roleCheck];
  return normalizedPlatformRoles(user).some((role) => rolesToCheck.includes(role));
}

export function hasPlatformAdminAccess(user: RoleAccessUser | null | undefined) {
  return hasPlatformRole(user, ['PlatformOwner', 'PlatformAdmin']) || hasLegacyPlatformCompat(user, ['Owner', 'Admin']);
}

export function hasPlatformOwnerAccess(user: RoleAccessUser | null | undefined) {
  return hasPlatformRole(user, 'PlatformOwner') || hasLegacyPlatformCompat(user, 'Owner');
}

export function hasTenantAdminAccess(
  user: RoleAccessUser | null | undefined,
  tenantRoles: TenantRole[] = [],
) {
  return Boolean(
    user &&
    (
      tenantRoles.includes('InstitutionAdmin') ||
      hasPlatformAdminAccess(user)
    )
  );
}

export function hasAdminAccess(
  user: RoleAccessUser | null | undefined,
  tenantRoles: TenantRole[] = [],
) {
  return hasTenantAdminAccess(user, tenantRoles);
}

export function hasInstructorAccess(
  user: RoleAccessUser | null | undefined,
  tenantRoles: TenantRole[] = [],
) {
  return Boolean(
    user &&
    (
      (user.roles ?? []).includes('Instructor') ||
      tenantRoles.some((role) => ['Instructor', 'ContentManager', 'InstitutionAdmin'].includes(role)) ||
      hasPlatformAdminAccess(user)
    )
  );
}

/**
 * Who may manage live-class scheduling and attendance. Mirrors the backend
 * `SCHEDULING_ROLES` (Instructor, Admin, Owner) — deliberately NOT
 * `ContentManager`, which the backend does not recognize as instructional staff
 * and rejects on every scheduling/attendance endpoint.
 */
export function hasSchedulingAccess(
  user: RoleAccessUser | null | undefined,
  tenantRoles: TenantRole[] = [],
) {
  return Boolean(
    user &&
    (
      (user.roles ?? []).includes('Instructor') ||
      tenantRoles.includes('Instructor') ||
      hasAdminAccess(user, tenantRoles) ||
      hasPlatformOwnerAccess(user)
    )
  );
}

export function hasAdministrativeProfile(user: RoleAccessUser | null | undefined) {
  return hasPlatformAdminAccess(user);
}
